import { Divider, Grid, styled, Typography } from '@mui/material';
import { LwButton } from 'redesign';
import { IconAdd, IconInfoRedesigned } from 'assets/img';
import { PreferredWorkerOptions } from 'api/services/shift/schema/preferred-worker-options.schema';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getWorkerFullName } from 'shared/utils/worker';
import { useFormArray } from 'shared/hooks/use-form-array';
import { PreferredWorkerRule } from './preferred-worker-rule';
import { PreferredWorkerFormContext } from './preferred-worker.types';
import { WorkerOption } from './inputs/preferred-worker-multiselect';

type Props = {
  preferredWorkerOptions: PreferredWorkerOptions | undefined;
  fieldsDisabled?: boolean;
  isLoadingWorkerOptions?: boolean;
  onWorkerSearchChange?: (search: string) => void;
};

export const PreferredWorkerWrapper = ({
  preferredWorkerOptions,
  fieldsDisabled,
  isLoadingWorkerOptions,
  onWorkerSearchChange,
}: Props) => {
  const { t } = useTranslation('web-common');
  const {
    fields,
    appendElement,
    deleteElement,
    arrayValue: visibilityRules,
  } = useFormArray<PreferredWorkerFormContext, 'visibilityRules'>('visibilityRules', {
    workerOptions: [],
    durationCondition: null,
  });
  const allSelectedWorkerOptions = visibilityRules.flatMap((rule) => rule.workerOptions);
  const lastRule = visibilityRules[visibilityRules.length - 1];
  const isLastRuleIncomplete = lastRule
    ? lastRule.workerOptions.length === 0 || !lastRule.durationCondition
    : false;
  const isAddButtonDisabled = fieldsDisabled || isLastRuleIncomplete;

  const workerOptionsMapped = useMemo(() => {
    if (!preferredWorkerOptions) {
      return [];
    }
    return mapPreferredWorkerOptions(preferredWorkerOptions);
  }, [preferredWorkerOptions, visibilityRules.length]);

  const workerOptionsWithoutSelected = useMemo(() => {
    if (allSelectedWorkerOptions.length !== 0) {
      return workerOptionsMapped.filter(
        (item) =>
          !visibilityRules.some((rule) =>
            rule.workerOptions.some((option) => option.id === item.id)
          )
      );
    }
    return workerOptionsMapped;
  }, [allSelectedWorkerOptions.length, visibilityRules.length, preferredWorkerOptions]);

  return (
    <Grid container item columnSpacing={4} xs={12}>
      {fields.map((field, index) => (
        <>
          <PreferredWorkerRule
            key={field.id}
            index={index}
            workerOptions={workerOptionsWithoutSelected}
            handleDelete={deleteElement}
            disabled={fieldsDisabled}
            isLoadingWorkerOptions={isLoadingWorkerOptions}
            onWorkerSearchChange={onWorkerSearchChange}
          />
          {index < fields.length - 1 ? (
            <StyledDividerContainer item xs={12}>
              <Divider />
            </StyledDividerContainer>
          ) : null}
        </>
      ))}

      <Grid item xs={12} display="flex" justifyContent="center" mt={2} mb={2}>
        <LwButton
          startIcon={<IconAdd width={18} height={18} />}
          color="tertiary"
          onClick={() => appendElement()}
          disabled={isAddButtonDisabled}
        >
          {t('work-form.preferred-worker.add-condition.label')}
        </LwButton>
      </Grid>

      <StyledDividerContainer item xs={12}>
        <Divider />
      </StyledDividerContainer>

      <Grid item container flexDirection={'row'}>
        <Grid container item xs={12} flexDirection={'row'}>
          <Grid item xs={1} columnSpacing={0}>
            <IconInfoRedesigned />
          </Grid>
          <Grid item xs={11}>
            <Typography variant="subtitle1">{t('work-form.preferred-worker.info.text')}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapPreferredWorkerOptions = (
  preferredWorkerOptions: PreferredWorkerOptions
): WorkerOption[] => {
  return preferredWorkerOptions.map((worker) => ({
    id: worker.workerId,
    name: getWorkerFullName({
      firstName: worker.firstName,
      lastName: worker.lastName,
      infix: worker.infix,
    }),
    description: worker.companyName ?? undefined,
    employmentType: worker.employmentType,
    workerType: worker.workerType,
  }));
};

const StyledDividerContainer = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));
